import React from 'react';
import { CheckCircle, PartyPopper, Handshake, Smile } from 'lucide-react';

const teamMembers = [
	{
		name: 'Sarah Johnson',
		title: 'Founder & Creative Director',
		avatar: 'https://source.unsplash.com/random/150x150/?woman,portrait,1',
		bio: "With over 15 years in event planning, Sarah founded Inder Events with a vision to create memorable celebrations that reflect each client's unique style.",
	},
	{
		name: 'Michael Thompson',
		title: 'Event Operations Manager',
		avatar: 'https://source.unsplash.com/random/150x150/?man,portrait,1',
		bio: 'Michael ensures flawless execution of every event, coordinating vendors, timelines, and logistics with precision and attention to detail.',
	},
	{
		name: 'Jennifer Lee',
		title: 'Design Specialist',
		avatar: 'https://source.unsplash.com/random/150x150/?woman,portrait,2',
		bio: "Jennifer transforms spaces with innovative design concepts, creating stunning visual experiences tailored to each client's vision and theme.",
	},
	{
		name: 'David Rodriguez',
		title: 'Client Relations Manager',
		avatar: 'https://source.unsplash.com/random/150x150/?man,portrait,2',
		bio: 'David is dedicated to understanding client needs and ensuring satisfaction throughout the planning process and beyond.',
	},
];

const coreValues = [
	{
		icon: <PartyPopper className="w-8 h-8 text-primary" />,
		title: 'Creativity',
		description:
			'We approach each event with fresh ideas and innovative solutions to create unique experiences.',
	},
	{
		icon: <Handshake className="w-8 h-8 text-primary" />,
		title: 'Reliability',
		description:
			'We deliver on our promises and handle every detail with professionalism and care.',
	},
	{
		icon: <Smile className="w-8 h-8 text-primary" />,
		title: 'Client Satisfaction',
		description:
			'Your vision and happiness are our top priorities from concept to execution.',
	},
];

export default function About() {
	return (
		<div className="w-full px-2 sm:px-4 md:px-12 xl:px-32 py-8">
			{/* Company History */}
			<section className="mb-12">
				<div className="flex flex-col md:flex-row gap-8 items-center">
					<div className="flex-1">
						<h1 className="text-3xl md:text-5xl font-bold mb-4 text-primary">
							About Inder Events
						</h1>
						<p className="mb-4 text-base md:text-lg">
							Founded in 2015, Inder Events has grown from a small boutique event
							planning service to one of the most sought-after event management
							companies in the region. Our journey began with a simple mission: to
							create extraordinary events that reflect our clients' unique visions.
						</p>
						<p className="mb-4 text-base md:text-lg">
							Over the years, we've had the privilege of planning and executing
							hundreds of successful events, from intimate gatherings to grand
							celebrations and high-profile corporate functions. Our dedicated team
							brings creativity, attention to detail, and professionalism to every
							project.
						</p>
						<p className="text-base md:text-lg">
							We pride ourselves on building lasting relationships with our clients,
							many of whom return to us for multiple events. Our commitment to
							excellence and personalized service has made Inder Events a trusted
							name in the event planning industry.
						</p>
					</div>
					<div className="flex-1 flex justify-center">
						<img
							src="https://source.unsplash.com/random/600x400/?event,planning,team"
							alt="Inder Events Team"
							className="rounded-xl shadow-lg w-full max-w-md object-cover"
						/>
					</div>
				</div>
			</section>

			{/* Core Values */}
			<section className="mb-12 bg-primary/10 rounded-xl p-6 md:p-10 text-white">
				<h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-primary">
					Our Core Values
				</h2>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
					{coreValues.map((value, i) => (
						<div
							key={i}
							className="flex flex-col items-center text-center bg-white/10 rounded-lg p-6"
						>
							<div className="mb-4">{value.icon}</div>
							<h3 className="text-xl font-semibold mb-2 text-primary-foreground">
								{value.title}
							</h3>
							<p className="text-base text-white/90">
								{value.description}
							</p>
						</div>
					))}
				</div>
			</section>

			{/* What We Offer */}
			<section className="mb-12">
				<h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-primary">
					What We Offer
				</h2>
				<p className="text-center mb-8 text-base md:text-lg">
					At Inder Events, we provide comprehensive event planning services
					tailored to your needs.
				</p>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
					<ul className="space-y-4">
						<li className="flex items-start gap-3">
							<CheckCircle className="text-primary mt-1" />{' '}
							<span>
								<b>Full-Service Event Planning</b>
								<br />
								<span className="text-gray-600">
									End-to-end management of all event details
								</span>
							</span>
						</li>
						<li className="flex items-start gap-3">
							<CheckCircle className="text-primary mt-1" />{' '}
							<span>
								<b>Venue Selection & Coordination</b>
								<br />
								<span className="text-gray-600">
									Access to exclusive venues and comprehensive site management
								</span>
							</span>
						</li>
						<li className="flex items-start gap-3">
							<CheckCircle className="text-primary mt-1" />{' '}
							<span>
								<b>Custom Design & Décor</b>
								<br />
								<span className="text-gray-600">
									Personalized themes and stunning visual elements
								</span>
							</span>
						</li>
					</ul>
					<ul className="space-y-4">
						<li className="flex items-start gap-3">
							<CheckCircle className="text-primary mt-1" />{' '}
							<span>
								<b>Vendor Management</b>
								<br />
								<span className="text-gray-600">
									Coordination with trusted vendors and service providers
								</span>
							</span>
						</li>
						<li className="flex items-start gap-3">
							<CheckCircle className="text-primary mt-1" />{' '}
							<span>
								<b>Budget Management</b>
								<br />
								<span className="text-gray-600">
									Strategic planning to maximize your event budget
								</span>
							</span>
						</li>
						<li className="flex items-start gap-3">
							<CheckCircle className="text-primary mt-1" />{' '}
							<span>
								<b>Day-of Coordination</b>
								<br />
								<span className="text-gray-600">
									On-site management ensuring flawless execution
								</span>
							</span>
						</li>
					</ul>
				</div>
			</section>

			{/* Team Section */}
			<section className="mb-12">
				<h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-primary">
					Meet Our Team
				</h2>
				<p className="text-center mb-8 text-base md:text-lg">
					Our experienced professionals are passionate about creating perfect
					events.
				</p>
				<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
					{teamMembers.map((member, i) => (
						<div
							key={i}
							className="flex flex-col items-center bg-white rounded-xl shadow p-6 text-center"
						>
							<img
								src={member.avatar}
								alt={member.name}
								className="w-28 h-28 rounded-full mb-4 object-cover shadow"
							/>
							<h3 className="text-lg font-semibold mb-1 text-primary-foreground">
								{member.name}
							</h3>
							<div className="text-primary text-sm mb-2">
								{member.title}
							</div>
							<p className="text-gray-600 text-sm">{member.bio}</p>
						</div>
					))}
				</div>
			</section>
		</div>
	);
}
